<template>
  <el-button
    v-bind="$attrs"
    :disabled="disabled || exporting"
    :loading="exporting"
    @click="exportData"
  >
    <slot />
    <template v-if="!$slots.default">{{ $t('exportText') }}</template>
  </el-button>
</template>

<script>
import { exportFile } from '@/utils/index';
import {
  buildFrontendExportData,
  normalizeFrontendExportFileName,
  resolveFrontendExportLabelList,
} from '@/utils/frontend-export';

export default {
  name: 'BjFrontendExport',
  inheritAttrs: false,
  props: {
    labelList: {
      type: Array,
      required: true,
    },
    rows: {
      type: Array,
      default: () => [],
    },
    getRows: {
      type: Function,
      default: null,
    },
    validateExport: {
      type: Function,
      default: null,
    },
    fileName: {
      type: String,
      default: '',
    },
    autoFormatDate: {
      type: Boolean,
      default: true,
    },
    translateLabel: {
      type: Boolean,
      default: false,
    },
    worksheet: {
      type: String,
      default: 'Sheet1',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      exporting: false,
    };
  },
  methods: {
    getSortableColumnList() {
      const visited = [];
      const contexts = [
        this.$vnode && this.$vnode.context,
        this.$parent,
      ].filter(Boolean);

      while (contexts.length) {
        const vm = contexts.shift();
        if (!vm || visited.includes(vm)) continue;
        visited.push(vm);

        const columnList = vm.SortableTable && vm.SortableTable.columnList;
        if (Array.isArray(columnList) && columnList.length) return columnList;
        if (Array.isArray(vm.dropCols) && vm.dropCols.length) return vm.dropCols;
        if (vm.$parent) contexts.push(vm.$parent);
      }

      return [];
    },
    async exportData() {
      if (this.disabled || this.exporting) return;
      this.exporting = true;
      try {
        if (typeof this.validateExport === 'function') {
          const validationResult = await this.validateExport(this.rows);
          if (validationResult === false) return;
          if (typeof validationResult === 'string' && validationResult) {
            this.$message.warning(validationResult);
            return;
          }
        }

        const rowSource = typeof this.getRows === 'function' ? await this.getRows() : this.rows;
        const rows = Array.isArray(rowSource) ? rowSource : [];
        if (!rows.length) {
          const message = this.emptyText || this.$t('ThreePartySalesOrder.selectDataWarning');
          this.$message.warning(message);
          return;
        }

        const cusId = this.$store && this.$store.getters ? this.$store.getters.cusId : null;
        const context = {
          role: cusId ? 'user' : 'admin',
          translate: value => this.$t(value),
        };
        const fileName = normalizeFrontendExportFileName(this.fileName);
        const exportLabelList = resolveFrontendExportLabelList(
          this.labelList,
          this.getSortableColumnList(),
        );
        const payload = buildFrontendExportData(exportLabelList, rows, context, {
          autoFormatDate: this.autoFormatDate,
          translateLabel: this.translateLabel,
        });
        if (fileName.toLowerCase().endsWith('.csv')) {
          exportFile(fileName, { fields: payload.headers, data: payload.data });
        } else {
          const records = payload.data.map(values => values.reduce((record, value, index) => {
            record[`column_${index}`] = value;
            return record;
          }, {}));
          exportFile(fileName, records, payload.headers, this.worksheet);
        }
        this.$emit('export-success');
      } catch (error) {
        this.$message.error(error && error.message ? error.message : 'Export failed');
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>
